import { SecureFields } from '@gr4vy/secure-fields-react'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { addFlashMessage } from '~/actions'
import { GR4VY_ID, GR4VY_ENVIRONMENT } from '~/config'
import useFeatureFlags from '~/hooks/use-feature-flags'
import { i18n } from '~/i18n'

import Gr4vyCheckoutSessionContext from './gr4vy-checkout-session.context'
import {
  useGr4vyCheckoutSessionId,
  reloadGr4vyCheckoutSessionId,
} from './gr4vy-checkout-session.utils'

import type { ReactNode } from 'react'

export type FormHandle = {
  onSubmit: () => void
  onFormChange: (data: object) => void
  onCardVaultSuccess: () => void
}

type Props = {
  children: ReactNode
  profileId: string
}

function Gr4vyCheckoutSessionProvider({ children, profileId }: Props) {
  const formRef = useRef<FormHandle>(null)
  const globalDispatch = useDispatch() // For top-level redux state
  const [ready, setReady] = useState(false)
  const {
    data: { checkoutSessionId, expiresAt, cardInformationVaulted } = {},
  } = useGr4vyCheckoutSessionId(profileId)

  // Reload the checkout session id after it expires
  useEffect(() => {
    if (!expiresAt) return

    const now = Date.now()
    const delay = Date.parse(expiresAt) - now

    if (delay <= 0) {
      reloadGr4vyCheckoutSessionId(profileId)
    } else {
      const timeoutId = setTimeout(
        () => reloadGr4vyCheckoutSessionId(profileId),
        delay,
      )

      // Cleanup timeout if `expiresAt` changes
      return () => clearTimeout(timeoutId)
    }
  }, [expiresAt])

  const value = {
    checkoutSessionId,
    cardInformationVaulted,
    ready,
    formRef,
    profileId,
  }

  return (
    <Gr4vyCheckoutSessionContext.Provider value={value}>
      <SecureFields
        gr4vyId={GR4VY_ID}
        environment={GR4VY_ENVIRONMENT}
        sessionId={checkoutSessionId}
        onReady={() => setReady(true)}
        onFormChange={(data) => formRef.current?.onFormChange(data)}
        onCardVaultSuccess={() => formRef.current?.onCardVaultSuccess()}
        onCardVaultFailure={() =>
          globalDispatch(
            addFlashMessage(
              'error',
              i18n.t('components.flash_messages.default_error'),
            ),
          )
        }
      >
        {children}
      </SecureFields>
    </Gr4vyCheckoutSessionContext.Provider>
  )
}

export default function Gr4vyCheckoutSessionProviderWrapper({
  profileId,
  children,
}: Props) {
  const { gr4vyEnabled } = useFeatureFlags()

  if (gr4vyEnabled) {
    return (
      <Gr4vyCheckoutSessionProvider profileId={profileId} children={children} />
    )
  } else {
    return <>{children}</>
  }
}
